import { CompositeLayer, RGBAColor } from "@deck.gl/core";
import { MVTLayer } from "@deck.gl/geo-layers";
// @ts-expect-error - missing type in third party types
import { DataFilterExtension, MaskExtension } from "@deck.gl/extensions";
import type { MVTLayerProps } from "@deck.gl/geo-layers/mvt-layer/mvt-layer";
import { TRANSPARENT, PROPRIETOR_COLOR_MAP } from "react-migration/domains/ownership/util/Colors";
import { getPermissions, hasBetaFeature } from "src/js/stores/user/actions";
import { Feature } from "src/js/stores/user";
import type { Geometry, OwnershipFeature } from "../../typings";
import { SelectedTitleLayer } from "./SelectedTitleLayer";

export enum OWNERSHIP_LAYERS {
  OWNERSHIP = "ownership",
}

export type FeatureFilter = (ownershipFeature: OwnershipFeature) => boolean;

export interface OwnershipLayerProps extends MVTLayerProps<OwnershipFeature> {
  displayKnownOwners: boolean;
  featureFilter: FeatureFilter;
  lineColor: RGBAColor;
  lineWidth: number;
  boundaryOpacity?: number;
  fillOpacityMultiplier: number;
  parcelSizeMin: number;
  parcelSizeMax: number;
  selection: Geometry[];
  layers: OWNERSHIP_LAYERS[];
  tileUrl?: string;
  pickable?: boolean;
}

export class OwnershipLayer extends CompositeLayer<OwnershipFeature, OwnershipLayerProps> {
  static componentName = "OwnershipLayer";

  renderLayers() {
    const geofenceGeometries = getPermissions()?.geofencesGeometries;
    const maskExtensions = [] as any[];
    let maskId: string | undefined;

    if (geofenceGeometries?.length && !hasBetaFeature(Feature.disableGeofence)) {
      maskExtensions.push(new MaskExtension());
      maskId = "Geofence";
    }
    const {
      id,
      displayKnownOwners,
      lineColor,
      lineWidth,
      boundaryOpacity,
      parcelSizeMin,
      parcelSizeMax,
      minZoom,
      maxZoom,
      data,
      tileUrl,
      selection,
      layers,
      featureFilter,
      pickable,
    } = this.props;

    return [
      new MVTLayer<OwnershipFeature>({
        ...this.getSubLayerProps({ id: `${id}__mvt` }),
        pickable: pickable ?? true,
        data: data && (data as Array<OwnershipFeature>)?.length ? data : tileUrl,
        stroked: true,
        filled: true,
        binary: true,
        minZoom,
        maxZoom,
        opacity: boundaryOpacity ?? 1.0,
        loadOptions: {
          mvt: {
            layers,
          },
        },
        lineWidthUnits: "pixels",
        getLineWidth: lineWidth,
        getLineColor: lineColor,
        getFillColor: (d: OwnershipFeature) => {
          if (!displayKnownOwners) return TRANSPARENT;
          if (!d.properties.proprietorship) return TRANSPARENT;

          const [r, g, b, a] = PROPRIETOR_COLOR_MAP[d.properties.proprietorship] ?? TRANSPARENT;

          return [r, g, b, (a || 0) * this.props.fillOpacityMultiplier];
        },
        updateTriggers: {
          getFillColor: [displayKnownOwners, this.props.fillOpacityMultiplier],
          getFilterValue: [featureFilter],
        },
        getFilterValue: (d: OwnershipFeature) => getFiltersValues(d, featureFilter),
        filterRange: [
          [1, 1],
          [parcelSizeMin, parcelSizeMax],
        ],
        extensions: [new DataFilterExtension({ filterSize: 2 }), ...maskExtensions],
        maskId,
      } as MVTLayerProps<OwnershipFeature>),
      new SelectedTitleLayer({
        ...this.getSubLayerProps({ id: `${id}__selection` }),
        selection,
        parcelSizeMin,
        parcelSizeMax,
      }),
    ];
  }
}

const getFiltersValues = (ownershipFeature: OwnershipFeature, featureFilter: FeatureFilter) => {
  return [featureFilter(ownershipFeature) ? 1 : 0, ownershipFeature.properties.area];
};
